import { GoogleGenerativeAI } from "@google/generative-ai";
import mammoth from 'mammoth';

// Define the structure of parsed resume data
export interface ParsedResume {
  personalInfo?: {
    fullName?: string;
    email?: string;
    phone?: string;
    location?: string;
    title?: string;
    summary?: string;
    linkedin?: string;
    github?: string;
    website?: string;
  };
  experience?: Array<{
    company?: string;
    position?: string;
    startDate?: string;
    endDate?: string | undefined;
    current?: boolean;
    location?: string;
    description?: string;
  }>;
  education?: Array<{
    institution?: string;
    degree?: string;
    fieldOfStudy?: string;
    startDate?: string;
    endDate?: string | undefined;
    current?: boolean;
    location?: string;
    description?: string;
  }>;
  skills?: Array<{
    name: string;
    category?: string;
  }>;
}

// Configuration for Gemini API
const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

// Initialize Gemini AI only if API key is present
const genAI = GEMINI_API_KEY 
  ? new GoogleGenerativeAI(GEMINI_API_KEY)
  : null;

// Gemini model configuration
const MODELS = {
  DEFAULT: 'gemini-2.0-flash-lite'
};

// Save the model for reuse
const defaultModel = genAI 
  ? genAI.getGenerativeModel({ model: MODELS.DEFAULT }) 
  : null;

/**
 * Check if Gemini is configured and ready to use
 * @returns boolean indicating if Gemini is configured
 */
export function isGeminiConfigured(): boolean {
  return !!genAI && !!defaultModel;
}

/**
 * Convert a file to base64 string
 * @param file - File object to convert
 * @returns Base64 encoded string of the file
 */
async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64String = (reader.result as string).split(',')[1];
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Extract text from PDF file
 * @param file - PDF file to extract text from
 * @returns Extracted text from the PDF
 */
async function extractPDFText(file: File): Promise<string> {
  const text = await file.text();
  return text.trim();
}

/**
 * Parse resume using Gemini API
 * @param file - Resume file to parse
 * @returns Parsed resume data
 */
export async function parseResume(file: File): Promise<ParsedResume> {
  // Check if Gemini is configured
  if (!isGeminiConfigured()) {
    throw new Error('Gemini AI is not configured. Please set the VITE_GEMINI_API_KEY environment variable.');
  }

  // Validate file type
  const fileType = file.type;
  
  try {
    let extractedText = '';
    let base64String = '';

    // Handle different file types
    switch (true) {
      case fileType === 'application/pdf':
        extractedText = await extractPDFText(file);
        base64String = await readFileAsBase64(file);
        break;
      
      case fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
           fileType === 'application/msword':
        const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
        extractedText = result.value;
        break;
      
      case fileType.startsWith('text/'):
        extractedText = await file.text();
        break;
      
      default:
        throw new Error(`Unsupported file type: ${fileType}`);
    }

    // Prepare prompt for resume parsing
    const prompt = `
    You are a resume parsing expert. Extract information from the provided resume text and return a JSON object that strictly matches this TypeScript interface:

    interface ParsedResume {
      personalInfo?: {
        fullName?: string;
        email?: string;
        phone?: string;
        location?: string;
        title?: string;
        summary?: string;
        linkedin?: string;
        github?: string;
        website?: string;
      };
      experience?: Array<{
        company?: string;
        position?: string;
        startDate?: string;
        endDate?: string;
        current?: boolean;
        location?: string;
        description?: string;
      }>;
      education?: Array<{
        institution?: string;
        degree?: string;
        fieldOfStudy?: string;
        startDate?: string;
        endDate?: string;
        current?: boolean;
        location?: string;
        description?: string;
      }>;
      skills?: Array<{
        name: string;
        category?: string;
      }>;
    }

    IMPORTANT RULES:
    1. Return ONLY a valid JSON object matching the interface above
    2. Do NOT include any explanatory text, markdown formatting, or code blocks
    3. Use undefined or empty arrays for missing sections
    4. Format dates as YYYY-MM-DD
    5. For current positions/education, set current: true and endDate: undefined
    6. Ensure all property names exactly match the interface
    7. Do not add any properties not defined in the interface
    8. Validate the JSON structure before returning

    EXAMPLE RESPONSE:
    {
      "personalInfo": {
        "fullName": "John Doe",
        "email": "john@example.com"
      },
      "experience": [{
        "company": "Tech Corp",
        "position": "Software Engineer",
        "startDate": "2020-01-01",
        "current": true
      }],
      "education": [],
      "skills": [
        {"name": "JavaScript", "category": "Programming"}
      ]
    }

    Now parse the following resume text and return a valid JSON object:

    ${extractedText}
    `;

    // Prepare the request
    let response;
    if (fileType === 'application/pdf') {
      response = await defaultModel!.generateContent({
        contents: [
          {
            role: 'user',
            parts: [
              { text: prompt },
              { 
                inlineData: { 
                  mimeType: 'application/pdf', 
                  data: base64String 
                } 
              }
            ]
          }
        ]
      });
    } else {
      response = await defaultModel!.generateContent(prompt);
    }

    // Get the response text
    const text = response.response.text().trim();
    
    // Try to parse the JSON response
    try {
      // Remove any potential markdown code block markers
      const cleanJson = text
        .replace(/^```json\s*/gim, '')
        .replace(/^```\s*/gim, '')
        .replace(/\s*```$/gim, '')
        .trim();

      const parsedData: ParsedResume = JSON.parse(cleanJson);

      // Validate the parsed data structure
      if (typeof parsedData !== 'object') {
        throw new Error('Response is not an object');
      }

      // Basic validation of required structure
      if (parsedData.personalInfo && typeof parsedData.personalInfo !== 'object') {
        throw new Error('Invalid personalInfo structure');
      }

      if (parsedData.experience && !Array.isArray(parsedData.experience)) {
        throw new Error('Invalid experience structure');
      }

      if (parsedData.education && !Array.isArray(parsedData.education)) {
        throw new Error('Invalid education structure');
      }

      if (parsedData.skills && !Array.isArray(parsedData.skills)) {
        throw new Error('Invalid skills structure');
      }

      return parsedData;
    } catch (jsonError) {
      console.error('Failed to parse Gemini response as JSON:', text);
      throw new Error('Invalid JSON response from Gemini. Please try again.');
    }
  } catch (error) {
    console.error('Resume parsing error:', error);
    throw error;
  }
}